import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { ChevronDown, Check, Search } from "lucide-react";

export interface CustomSelectOption {
  value: string;
  label: string;
  sublabel?: string;
}

interface CustomSelectProps {
  value: string;
  options: CustomSelectOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Show a search box inside the panel when there are many options. */
  searchable?: boolean;
  className?: string;
}

/**
 * Professional custom dropdown — replaces the native <select>. Renders the
 * options panel in a portal so it never gets clipped by overflow containers
 * (modals, scroll areas). Click-outside + Escape close it.
 *
 * Lives under workflow/common/node-ui — shared UI used only by workflow nodes.
 */
export const CustomSelect: React.FC<CustomSelectProps> = ({
  value,
  options,
  onChange,
  placeholder = "Select…",
  disabled = false,
  searchable = false,
  className = "",
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [coords, setCoords] = useState<{
    left: number;
    width: number;
    /** Either top (open downward) or bottom (open upward) is set. */
    top?: number;
    bottom?: number;
    maxHeight: number;
  } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const selected = options.find((o) => o.value === value);

  const recompute = () => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const margin = 8;
    const preferred = 288; // ideal panel height
    const spaceBelow = window.innerHeight - r.bottom - margin;
    const spaceAbove = r.top - margin;

    // Open downward when there's room; otherwise flip up if there's more room
    // above. Either way cap the height to the available space so the panel is
    // never cut off below the popup / viewport.
    if (spaceBelow >= Math.min(preferred, 180) || spaceBelow >= spaceAbove) {
      setCoords({ left: r.left, width: r.width, top: r.bottom + 4, maxHeight: Math.max(120, Math.min(preferred, spaceBelow)) });
    } else {
      setCoords({ left: r.left, width: r.width, bottom: window.innerHeight - r.top + 4, maxHeight: Math.max(120, Math.min(preferred, spaceAbove)) });
    }
  };

  useEffect(() => {
    if (!open) return;
    recompute();
    const onScrollResize = () => recompute();
    const onClick = (e: MouseEvent) => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(e.target as any) &&
        panelRef.current &&
        !panelRef.current.contains(e.target as any)
      ) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("scroll", onScrollResize, true);
    window.addEventListener("resize", onScrollResize);
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("scroll", onScrollResize, true);
      window.removeEventListener("resize", onScrollResize);
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const filtered =
    searchable && search.trim()
      ? options.filter(
          (o) =>
            o.label.toLowerCase().includes(search.toLowerCase()) ||
            (o.sublabel || "").toLowerCase().includes(search.toLowerCase()),
        )
      : options;

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation();
          if (!disabled) setOpen((v) => !v);
        }}
        className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-sm text-left bg-white border border-gray-300 rounded-md outline-none transition-colors hover:border-gray-400 focus:ring-2 focus:ring-gray-400/40 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed ${className}`}
      >
        <span
          className={`truncate ${selected ? "text-gray-900" : "text-gray-400"}`}
        >
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDown
          className={`w-4 h-4 text-gray-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open &&
        coords &&
        ReactDOM.createPortal(
          <div
            ref={panelRef}
            style={{
              position: "fixed",
              ...(coords.top != null ? { top: coords.top } : { bottom: coords.bottom }),
              left: coords.left,
              width: coords.width,
              maxHeight: coords.maxHeight,
              // Above every workflow modal (main popup z-9999, nested
              // credential modal z-10001) so the panel is never clipped/hidden.
              zIndex: 100000,
            }}
            className="bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {searchable && (
              <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100">
                <Search className="w-3.5 h-3.5 text-gray-400" />
                <input
                  autoFocus
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search…"
                  className="flex-1 text-sm outline-none bg-transparent placeholder-gray-400"
                />
              </div>
            )}
            <div className="flex-1 min-h-0 overflow-y-auto py-1 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full">
              {filtered.length === 0 ? (
                <div className="px-3 py-3 text-xs text-gray-400 text-center italic">
                  No options
                </div>
              ) : (
                filtered.map((o) => (
                  <button
                    key={o.value}
                    type="button"
                    onClick={() => {
                      onChange(o.value);
                      setOpen(false);
                      setSearch("");
                    }}
                    className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-sm text-left transition-colors ${o.value === value ? "bg-gray-100 text-gray-900" : "hover:bg-gray-50 text-gray-700"}`}
                  >
                    <span className="min-w-0">
                      <span className="block truncate">{o.label}</span>
                      {o.sublabel && (
                        <span className="block text-[11px] text-gray-400 truncate">
                          {o.sublabel}
                        </span>
                      )}
                    </span>
                    {o.value === value && (
                      <Check className="w-3.5 h-3.5 text-gray-600 shrink-0" />
                    )}
                  </button>
                ))
              )}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

export default CustomSelect;
